import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Cloud, ArrowLeft, ArrowRight, Check, Zap, Shield, BarChart3, Building2 } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLANS = [
  {
    id: 'small',
    name: 'Small',
    basePrice: 49.99,
    addonPrice: 9.99,
    maxRooms: 20,
    roomRange: '0-20',
    description: 'Perfect for boutique hotels & B&Bs',
    features: [
      'Up to 20 rooms',
      'Unlimited reservations',
      'Front desk & housekeeping',
      'Guest portal',
      'Email notifications',
    ],
    icon: Zap,
  },
  {
    id: 'medium',
    name: 'Medium',
    basePrice: 79.99,
    addonPrice: 19.99,
    maxRooms: 50,
    roomRange: '0-50',
    description: 'For growing properties',
    features: [
      'Up to 50 rooms',
      'Everything in Small',
      'Channel manager',
      'Advanced analytics',
      'Booking engine',
    ],
    icon: BarChart3,
    recommended: true,
  },
  {
    id: 'large',
    name: 'Large',
    basePrice: 119.99,
    addonPrice: 29.99,
    maxRooms: 120,
    roomRange: '0-120',
    description: 'For large hotels & resorts',
    features: [
      'Up to 120 rooms',
      'Everything in Medium',
      'Priority support',
      'Advanced forecasting',
      'Staff scheduling',
    ],
    icon: Building2,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    basePrice: null,
    addonPrice: null,
    maxRooms: 9999,
    roomRange: '120+',
    description: 'For hotel chains & large portfolios',
    features: [
      'Unlimited rooms',
      'Everything in Large',
      'Dedicated account manager',
      'Custom integrations & API access',
      'White-label option',
      'SLA guarantee',
    ],
    icon: Building2,
    isEnterprise: true,
  },
];

const StepIndicator = ({ currentStep }) => (
  <div className="flex items-center justify-center gap-3 mb-8">
    {[1, 2].map((step) => (
      <div key={step} className="flex items-center gap-2">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
            step === currentStep
              ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white'
              : step < currentStep
              ? 'bg-green-500 text-white'
              : 'bg-slate-200 text-slate-500'
          }`}
        >
          {step < currentStep ? <Check size={16} /> : step}
        </div>
        <span
          className={`text-sm font-medium ${
            step === currentStep ? 'text-slate-900' : 'text-slate-400'
          }`}
        >
          {step === 1 ? 'Account' : 'Plan'}
        </span>
        {step < 2 && (
          <div
            className={`w-12 h-0.5 mx-1 ${
              currentStep > 1 ? 'bg-green-500' : 'bg-slate-200'
            }`}
          />
        )}
      </div>
    ))}
  </div>
);

const PlanCard = ({ plan, selected, onSelect }) => {
  const Icon = plan.icon;
  return (
    <button
      type="button"
      onClick={() => onSelect(plan.id)}
      className={`relative w-full text-left rounded-xl border-2 p-5 transition-all ${
        selected
          ? 'border-cyan-500 bg-cyan-50/50 shadow-md ring-1 ring-cyan-200'
          : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
      }`}
    >
      {plan.recommended && (
        <span className="absolute -top-3 left-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
          Recommended
        </span>
      )}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              selected
                ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white'
                : 'bg-slate-100 text-slate-600'
            }`}
          >
            <Icon size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{plan.name}</h3>
            <p className="text-xs text-slate-500">{plan.description}</p>
          </div>
        </div>
        <div
          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1 ${
            selected ? 'border-cyan-500 bg-cyan-500' : 'border-slate-300'
          }`}
        >
          {selected && <Check size={12} className="text-white" />}
        </div>
      </div>
      <div className="mb-1">
        {plan.isEnterprise ? (
          <span className="text-2xl font-bold text-slate-900">Custom</span>
        ) : (
          <>
            <span className="text-2xl font-bold text-slate-900">${plan.basePrice}</span>
            <span className="text-sm text-slate-500">/mo per property</span>
          </>
        )}
      </div>
      <p className="text-xs text-slate-400 mb-3">
        {plan.isEnterprise
          ? 'Contact us for volume pricing'
          : `${plan.roomRange} rooms \u00b7 +$${plan.addonPrice}/mo per extra property`}
      </p>
      <ul className="space-y-1.5">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-center gap-2 text-sm text-slate-600">
            <Check size={14} className="text-green-500 shrink-0" />
            {feature}
          </li>
        ))}
      </ul>
    </button>
  );
};

const Signup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Step state
  const [step, setStep] = useState(1);

  // Step 1 fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgName, setOrgName] = useState('');

  // Step 2 fields
  const [selectedPlan, setSelectedPlan] = useState('medium');
  const [roomCount, setRoomCount] = useState(10);
  const [propertyCount, setPropertyCount] = useState(1);

  const [loading, setLoading] = useState(false);

  const selectedPlanData = PLANS.find((p) => p.id === selectedPlan);
  const monthlyTotal = useMemo(() => {
    if (!selectedPlanData) return 0;
    const additionalProperties = Math.max(0, propertyCount - 1);
    return selectedPlanData.basePrice + additionalProperties * selectedPlanData.addonPrice;
  }, [selectedPlanData, propertyCount]);

  const validatePassword = (pwd) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(pwd);
    const hasLowerCase = /[a-z]/.test(pwd);
    const hasNumber = /\d/.test(pwd);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

    const errors = [];
    if (pwd.length < minLength) errors.push('at least 8 characters');
    if (!hasUpperCase) errors.push('one uppercase letter');
    if (!hasLowerCase) errors.push('one lowercase letter');
    if (!hasNumber) errors.push('one number');
    if (!hasSpecialChar) errors.push('one special character (!@#$%^&* etc.)');

    return errors;
  };

  const handleNextStep = (e) => {
    e.preventDefault();

    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      toast.error(`Password must contain ${passwordErrors.join(', ')}`);
      return;
    }

    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const maxRooms = selectedPlanData?.maxRooms || 500;
    if (roomCount < 1 || roomCount > maxRooms) {
      toast.error(`Room count must be between 1 and ${maxRooms} for the ${selectedPlanData?.name} plan`);
      return;
    }
    if (propertyCount < 1) {
      toast.error('Property count must be at least 1');
      return;
    }

    // Enterprise: create account then redirect to contact page
    if (selectedPlanData?.isEnterprise) {
      setLoading(true);
      try {
        await axios.post(`${API}/auth/signup`, { name, email, password, org_name: orgName });
        toast.success('Account created! Our sales team will contact you within 24 hours.');
        navigate('/signup-success');
      } catch (err) {
        const detail = err.response?.data?.detail || err.response?.data?.error?.message || 'Signup failed';
        toast.error(typeof detail === 'string' ? detail : 'Signup failed. Please try again.');
        setStep(1);
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);

    try {
      // Step 1: Create the account
      await axios.post(`${API}/auth/signup`, {
        name,
        email,
        password,
        org_name: orgName,
      });

      // Step 2: Login to get a token
      const loginRes = await axios.post(`${API}/auth/login`, {
        email,
        password,
      });

      const token = loginRes.data.access_token;
      if (loginRes.data.refresh_token) {
        localStorage.setItem('refresh_token', loginRes.data.refresh_token);
      }
      localStorage.setItem('access_token', token);

      // Step 3: Create Stripe Checkout session
      const checkoutRes = await axios.post(
        `${API}/billing/create-checkout-session`,
        {
          plan: selectedPlan,
          room_count: roomCount,
          property_count: propertyCount,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Step 4: Redirect to Stripe Checkout
      if (checkoutRes.data.url) {
        window.location.href = checkoutRes.data.url;
      } else {
        toast.error('Failed to create checkout session');
        navigate('/signup-success', { state: { email } });
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (
        error.response?.status === 400 ||
        error.response?.status === 409 ||
        error.response?.status === 422
      ) {
        // Signup or validation error -- go back to step 1
        setStep(1);
        toast.error(detail || 'Signup failed. Please check your details.');
      } else {
        toast.error(detail || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center p-8 bg-gradient-to-br from-cyan-50 via-blue-50 to-slate-50 relative">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl mb-4">
                <Cloud className="text-white" size={32} />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                {step === 1 ? t('auth.getStarted') : 'Choose Your Plan'}
              </h1>
              <p className="text-slate-600">
                {step === 1 ? t('auth.createAccount') : 'Select a plan for your property'}
              </p>
            </div>

            <StepIndicator currentStep={step} />

            {step === 1 && (
              <form onSubmit={handleNextStep} className="space-y-4" data-testid="signup-form">
                <div>
                  <Label htmlFor="name">{t('auth.yourName')}</Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('placeholder.enterName')}
                    required
                    data-testid="signup-name-input"
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="orgName">{t('auth.orgName')}</Label>
                  <Input
                    id="orgName"
                    type="text"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder={t('form.organizationName')}
                    required
                    data-testid="signup-org-input"
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="email">{t('form.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.emailPlaceholder')}
                    required
                    data-testid="signup-email-input"
                    className="mt-1.5"
                  />
                </div>

                <div>
                  <Label htmlFor="password">{t('form.password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    data-testid="signup-password-input"
                    className="mt-1.5"
                  />
                  <p className="text-xs text-slate-500 mt-1.5">
                    {t('auth.passwordRules')}
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 gap-2"
                  data-testid="signup-next-button"
                >
                  Next
                  <ArrowRight size={16} />
                </Button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="signup-plan-form">
                <div className="space-y-3">
                  {PLANS.map((plan) => (
                    <PlanCard
                      key={plan.id}
                      plan={plan}
                      selected={selectedPlan === plan.id}
                      onSelect={setSelectedPlan}
                    />
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="roomCount">Number of Rooms</Label>
                    <Input
                      id="roomCount"
                      type="number"
                      min={1}
                      max={selectedPlanData?.maxRooms || 500}
                      value={roomCount}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        const max = selectedPlanData?.maxRooms || 500;
                        if (!isNaN(val)) {
                          setRoomCount(Math.min(max, Math.max(1, val)));
                        } else if (e.target.value === '') {
                          setRoomCount('');
                        }
                      }}
                      onBlur={() => {
                        if (roomCount === '' || roomCount < 1) setRoomCount(1);
                      }}
                      required
                      data-testid="signup-room-count-input"
                      className="mt-1.5"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Up to {selectedPlanData?.maxRooms || 500} rooms
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="propertyCount">Number of Properties</Label>
                    <Input
                      id="propertyCount"
                      type="number"
                      min={1}
                      value={propertyCount}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val)) {
                          setPropertyCount(Math.max(1, val));
                        } else if (e.target.value === '') {
                          setPropertyCount('');
                        }
                      }}
                      onBlur={() => {
                        if (propertyCount === '' || propertyCount < 1) setPropertyCount(1);
                      }}
                      required
                      data-testid="signup-property-count-input"
                      className="mt-1.5"
                    />
                    <p className="text-xs text-slate-500 mt-1">1 included, extras at add-on rate</p>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Monthly total</p>
                      <p className="text-sm text-slate-500 mt-0.5">
                        {selectedPlanData?.name} plan &middot; {propertyCount || 1} propert{propertyCount === 1 ? 'y' : 'ies'}
                      </p>
                      {propertyCount > 1 && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          ${selectedPlanData?.basePrice} base + {propertyCount - 1} &times; ${selectedPlanData?.addonPrice}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-slate-900">
                        ${monthlyTotal.toFixed(2)}
                      </p>
                      <p className="text-xs text-slate-500">/month + applicable taxes</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="gap-2"
                    data-testid="signup-back-button"
                  >
                    <ArrowLeft size={16} />
                    Back
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 gap-2"
                    disabled={loading}
                    data-testid="signup-submit-button"
                  >
                    {loading ? (
                      'Creating account...'
                    ) : (
                      <>
                        <Shield size={16} />
                        {selectedPlanData?.isEnterprise ? 'Create Account & Contact Sales' : 'Subscribe & Create Account'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}

            <p className="text-center mt-6 text-sm text-slate-600">
              {t('auth.hasAccount')}{' '}
              <Link to="/login" className="text-cyan-600 hover:text-cyan-700 font-medium" data-testid="login-link">
                {t('auth.signIn')}
              </Link>
            </p>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-cyan-600 via-blue-600 to-blue-700 items-center justify-center p-12">
        <div className="max-w-lg text-white">
          <h2 className="text-4xl font-bold mb-6">Join Keyless PMS</h2>
          <p className="text-xl text-cyan-50 mb-8">
            Start managing your properties with our modern, cloud-native PMS. Simple, transparent pricing that scales with your business.
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Check size={16} />
              </div>
              <span className="text-cyan-50">No long-term contracts</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Check size={16} />
              </div>
              <span className="text-cyan-50">Cancel anytime</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Check size={16} />
              </div>
              <span className="text-cyan-50">14-day free trial included</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
